import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { createStudent } from '../../../services/studentService';

const AddStudentForm = ({ classrooms, onAddStudent, onCancel }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    classroomId: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const classroomOptions = classrooms?.map(classroom => ({
    value: classroom?.id,
    label: classroom?.classroomName
  }));

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData?.classroomId) {
      newErrors.classroomId = 'Please select a classroom';
    }
    
    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      // Create student in Supabase
      const newStudent = await createStudent({
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        classroomId: formData?.classroomId,
        email: formData?.email || null
      });

      // Set generated credentials for display
      setGeneratedCredentials({
        userId: newStudent?.username,
        password: newStudent?.plainPassword
      });

      // Notify parent component
      onAddStudent(newStudent);
    } catch (err) {
      console.error('Error creating student:', err);
      setError(err?.message || 'Failed to create student');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      firstName: '',
      lastName: '',
      classroomId: '',
      email: ''
    });
    setErrors({});
    setGeneratedCredentials(null);
    setError(null);
  };

  if (generatedCredentials) {
    return (
      <div className="bg-card rounded-lg shadow-sm p-4 md:p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 md:w-20 md:h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-success" />
          </div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">Student Added Successfully!</h3>
          <p className="text-sm md:text-base text-muted-foreground">Credentials have been generated for the student</p>
        </div>
        <div className="bg-muted rounded-lg p-4 md:p-6 mb-6">
          <div className="space-y-4">
            <div>
              <label className="text-xs md:text-sm font-medium text-muted-foreground">User ID</label>
              <div className="flex items-center justify-between mt-1 p-3 bg-background rounded-md">
                <code className="text-sm md:text-base font-mono text-foreground">{generatedCredentials?.userId}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Copy"
                  onClick={() => navigator.clipboard?.writeText(generatedCredentials?.userId)}
                >
                  Copy
                </Button>
              </div>
            </div>
            
            <div>
              <label className="text-xs md:text-sm font-medium text-muted-foreground">Password</label>
              <div className="flex items-center justify-between mt-1 p-3 bg-background rounded-md">
                <code className="text-sm md:text-base font-mono text-foreground">{generatedCredentials?.password}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Copy"
                  onClick={() => navigator.clipboard?.writeText(generatedCredentials?.password)}
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" fullWidth onClick={handleReset}>
            Add Another Student
          </Button>
          <Button fullWidth onClick={onCancel}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">Add New Student</h3>
        <Button variant="ghost" size="sm" iconName="X" onClick={onCancel}>
          Close
        </Button>
      </div>
      
      {classrooms?.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-warning" />
          </div>
          <h4 className="text-lg font-semibold text-foreground mb-2">No Classrooms Available</h4>
          <p className="text-muted-foreground mb-6">
            You must create and save a classroom before you can add students.
          </p>
          <Button onClick={onCancel}>
            Close
          </Button>
        </div>
      ) : (
        <>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={18} className="text-red-600 mt-0.5 mr-2" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                id="student-firstName"
                name="firstName"
                autoComplete="given-name"
                placeholder="Enter first name"
                value={formData?.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e?.target?.value })}
                error={errors?.firstName}
                required
                disabled={loading}
              />

              <Input
                label="Last Name"
                type="text"
                id="student-lastName"
                name="lastName"
                autoComplete="family-name"
                placeholder="Enter last name"
                value={formData?.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e?.target?.value })}
                error={errors?.lastName}
                required
                disabled={loading}
              />
            </div>

            <Select
              label="Classroom"
              options={classroomOptions}
              value={formData?.classroomId}
              onChange={(value) => setFormData({ ...formData, classroomId: value })}
              error={errors?.classroomId}
              placeholder="Select a classroom"
              required
              disabled={loading}
            />

            <Input
              label="Email (Optional)"
              type="email"
              id="student-email"
              name="email"
              autoComplete="email"
              placeholder="student@example.com"
              description="Optional: For sending credentials via email"
              value={formData?.email}
              onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
              error={errors?.email}
              disabled={loading}
            />

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button type="button" variant="outline" fullWidth onClick={handleReset} disabled={loading}>
                Reset
              </Button>
              <Button type="submit" fullWidth iconName="UserPlus" disabled={loading}>
                {loading ? 'Creating...' : 'Generate Credentials'}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default AddStudentForm;